import ApplicationController from "../application_controller"

const DICTIONARY_DROPDOWN_STATE = Object.freeze({
  NONE_SELECTED: 'none_selected',
  ALL_SELECTED: 'all_selected',
  PARTIALLY_SELECTED: 'partially_selected'
});

const DICTIONARY_DROPDOWN_STATE_CLASS = Object.freeze({
  [DICTIONARY_DROPDOWN_STATE.ALL_SELECTED]: 'dictionary-dropdown-button-all-selected',
  // Intentionally same CSS class as ALL_SELECTED: both states use identical button typography (e.g. "All Dictionaries" / "No Dictionaries").
  [DICTIONARY_DROPDOWN_STATE.NONE_SELECTED]: 'dictionary-dropdown-button-all-selected',
  [DICTIONARY_DROPDOWN_STATE.PARTIALLY_SELECTED]: 'dictionary-dropdown-button-count-selected'
});

export default class extends ApplicationController {
  static targets = [
    'dictionaryDropdownButton',
    'dictionaryDropdownButtonLabel',
    'dictionaryDropdownMenu',
    'dictionarySelectAllRow',
    'dictionarySelectAllDivider',
    'dictionaryDropdownScrollArea',
    'dictionaryDropdownSelectAllCheckbox',
    'dictionarySelectionCheckbox',
    'dictionaryRowCheckbox',
    'dictionaryRowLabel',
    'statusLiveRegion'
  ]

  static values = {
    allDictionariesText: String,
    noDictionariesText: String,
    singleSelectedDictionaryTemplate: String,
    multipleSelectedDictionariesTemplate: String,
    dictionarySelectorLabel: String
  }

  connect() {
    this.initializeDictionaryDropdowns();
    this.refereshIcons();
  }

  initializeDictionaryDropdowns() {
    const selectAllCheckbox = this.dictionaryDropdownSelectAllCheckboxTarget;
    const selectIndividualDictionaryCheckboxes = this.dictionarySelectionCheckboxTargets;

    if (selectAllCheckbox && selectAllCheckbox.checked) {
      selectIndividualDictionaryCheckboxes.forEach((checkbox) => {
        checkbox.checked = true;
      });
    }

    this.syncAllCheckboxState();
    this.updateDictionaryDropdownButtonText();
  }

  handleDictionaryRowClick(event) {
    const clickedOnCheckbox = this.dictionaryRowCheckboxTargets.some((checkbox) => checkbox.contains(event.target));
    const clickedOnLabel = this.dictionaryRowLabelTargets.some((label) => label.contains(event.target));
    if (clickedOnCheckbox || clickedOnLabel) return;

    const row = event.currentTarget;
    const checkbox = this.dictionaryRowCheckboxTargets.find((target) => row.contains(target));
    if (!checkbox) return;

    checkbox.checked = !checkbox.checked;
    checkbox.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleDictionaryDropdownShow() {
    this.element.classList.add('dictionary-dropdown-active');
    this.updateDictionaryDropdownAvailableHeight();
    this._boundSyncDropdownHeight = () => this.updateDictionaryDropdownAvailableHeight();
    window.addEventListener('resize', this._boundSyncDropdownHeight);
    window.visualViewport?.addEventListener('resize', this._boundSyncDropdownHeight);
  }

  handleDictionaryDropdownHide() {
    this.element.classList.remove('dictionary-dropdown-active');
    if (!this._boundSyncDropdownHeight) return;

    window.removeEventListener('resize', this._boundSyncDropdownHeight);
    window.visualViewport?.removeEventListener('resize', this._boundSyncDropdownHeight);
    this._boundSyncDropdownHeight = null;
  }

  disconnect() {
    this.handleDictionaryDropdownHide();
  }

  updateDictionaryDropdownAvailableHeight() {
    if (!this.hasDictionaryDropdownMenuTarget || !this.hasDictionaryDropdownButtonTarget) return;

    const buttonRect = this.dictionaryDropdownButtonTarget.getBoundingClientRect();
    const viewportHeight = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
    const dropdownTopGap = 12;
    const availableHeight = Math.floor(viewportHeight - buttonRect.bottom - dropdownTopGap);
    const safeMaxHeight = Math.min(500, Math.max(140, availableHeight));

    this.dictionaryDropdownMenuTarget.style.setProperty('--dictionary-dropdown-available-height', `${safeMaxHeight}px`);
  }

  handleDictionaryToggleAll(event) {
    const checked = event.currentTarget.checked;
    this.dictionarySelectionCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = checked;
    });

    this.updateDictionaryDropdownButtonText();
  }

  handleDictionaryCollectionChange() {
    this.syncAllCheckboxState();
    this.updateDictionaryDropdownButtonText();
  }

  syncAllCheckboxState() {
    if (!this.hasDictionaryDropdownSelectAllCheckboxTarget || this.dictionarySelectionCheckboxTargets.length === 0) return;

    const checkedCount = this.dictionarySelectionCheckboxTargets.filter((checkbox) => checkbox.checked).length;
    this.dictionaryDropdownSelectAllCheckboxTarget.checked = checkedCount === this.dictionarySelectionCheckboxTargets.length;
  }

  updateDictionaryDropdownButtonText() {
    if (!this.hasDictionaryDropdownButtonTarget) return;
    if (!this.hasDictionaryDropdownButtonLabelTarget) return;

    const allText = this.allDictionariesTextValue || 'All Dictionaries';
    const selectedDictionariesCount = this.dictionarySelectionCheckboxTargets.filter((checkbox) => checkbox.checked).length;
    const totalCount = this.dictionarySelectionCheckboxTargets.length;
    const noDictionariesText = this.noDictionariesTextValue;
    const singleSelectedDictionaryTemplate = this.singleSelectedDictionaryTemplateValue;
    const multipleSelectedDictionariesTemplate = this.multipleSelectedDictionariesTemplateValue;

    const { state, text } = this.resolveDictionaryDropdownPresentation({
      selectAllCheckbox: this.dictionaryDropdownSelectAllCheckboxTarget,
      selectedDictionariesCount,
      totalCount,
      allText,
      noDictionariesText,
      singleSelectedDictionaryTemplate,
      multipleSelectedDictionariesTemplate
    });

    const button = this.dictionaryDropdownButtonTarget;
    this.dictionaryDropdownButtonLabelTarget.textContent = text;
    this.applyDictionaryDropdownButtonState(button, state);

    const labelPrefix = this.dictionarySelectorLabelValue || button.getAttribute('aria-label') || 'Select dictionaries to search';
    button.setAttribute('aria-label', `${labelPrefix}: ${text}`);

    if (this.hasStatusLiveRegionTarget) {
      this.statusLiveRegionTarget.textContent = text;
    }
  }

  applyDictionaryDropdownButtonState(button, state) {
    button.classList.remove(...Object.values(DICTIONARY_DROPDOWN_STATE_CLASS));

    const stateClass = DICTIONARY_DROPDOWN_STATE_CLASS[state];
    if (stateClass) {
      button.classList.add(stateClass);
    }
  }

  resolveDictionaryDropdownPresentation({
    selectAllCheckbox,
    selectedDictionariesCount,
    totalCount,
    allText,
    noDictionariesText,
    singleSelectedDictionaryTemplate,
    multipleSelectedDictionariesTemplate
  }) {
    if (selectAllCheckbox && selectAllCheckbox.checked) {
      return {
        state: DICTIONARY_DROPDOWN_STATE.ALL_SELECTED,
        text: allText
      };
    }

    if (selectedDictionariesCount === 0) {
      return {
        state: DICTIONARY_DROPDOWN_STATE.NONE_SELECTED,
        text: noDictionariesText
      };
    }

    if (selectedDictionariesCount === totalCount) {
      return {
        state: DICTIONARY_DROPDOWN_STATE.ALL_SELECTED,
        text: allText
      };
    }

    const label = selectedDictionariesCount === 1 ? singleSelectedDictionaryTemplate : multipleSelectedDictionariesTemplate;
    return {
      state: DICTIONARY_DROPDOWN_STATE.PARTIALLY_SELECTED,
      text: `${selectedDictionariesCount} ${label}`.trim()
    };
  }
}
